/**
 * The credential register.
 *
 * A list page like every other one in the CRM, with one thing none of the others
 * has: it can be switched OFF at the server. Without `CRM_VAULT_KEY` there is no
 * way to encrypt, so there is no way to store - and the page says exactly that
 * instead of letting the operator type a password into a form that will refuse
 * it. See `GET /api/admin/crm/vault/status`.
 *
 * No password is ever on this page. The rows carry "a password is on file" and
 * nothing more; reading one is a press with a dialog, a countdown and an audit
 * row behind it (`RevealDialog`).
 */
import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, KeyRound, Loader2, Plus, ShieldAlert } from "lucide-react";
import { useT } from "../../../i18n/I18nProvider";
import { userCanPlatform, type StaffUser } from "../../../lib/auth";
import {
  VAULT_KINDS,
  deleteVaultEntry,
  getVaultEntries,
  getVaultStatus,
  type VaultEntry,
  type VaultStatus,
} from "../../../lib/vault";
import {
  Btn,
  EmptyState,
  MenuField,
  Modal,
  PageHead,
  SearchField,
  Toolbar,
  listCard,
  useToast,
} from "../../ui";
import { PopHost } from "../../shell/Pop";
import { COLS, VaultRow } from "./VaultRow";
import { VaultModal } from "./VaultModal";
import { RevealDialog } from "./RevealDialog";
import { kindLabelKey } from "./kinds";

const PAGE_SIZE = 25;

export function VaultSection({ user }: { user: StaffUser }) {
  const t = useT();
  const toast = useToast();

  const [rows, setRows] = useState<VaultEntry[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState<VaultStatus | null>(null);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [kind, setKind] = useState("");
  const [page, setPage] = useState(1);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<VaultEntry | null>(null);
  const [revealing, setRevealing] = useState<VaultEntry | null>(null);
  const [toDelete, setToDelete] = useState<VaultEntry | null>(null);
  const [busy, setBusy] = useState(false);

  const canManage = userCanPlatform(user, "manage:crm.vault");

  useEffect(() => {
    const id = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(id);
  }, [qInput]);

  // Reset to the first page whenever the filter changes, or a narrowed list of
  // three rows read on page 2 would come back empty.
  useEffect(() => {
    setPage(1);
  }, [q, kind]);

  useEffect(() => {
    getVaultStatus()
      .then((s) => {
        setStatus(s);
        // The kinds the server knows against the kinds this app can label. A
        // mismatch is a mirror that has drifted (see lib/vault.ts) and would
        // show up as a row with a blank kind - said in the console rather than
        // left to be noticed by a customer.
        const unknown = s.kinds.filter((k) => !(VAULT_KINDS as readonly string[]).includes(k));
        if (unknown.length) console.warn("vault: server kinds not mirrored in web_crm", unknown);
      })
      .catch(() => setStatus(null));
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getVaultEntries({ q: q || undefined, kind: kind || undefined, page, pageSize: PAGE_SIZE });
      setRows(res.rows);
      setTotal(res.total);
    } catch {
      setRows([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [q, kind, page]);

  useEffect(() => {
    void load();
  }, [load]);

  async function confirmDelete() {
    if (!toDelete || busy) return;
    setBusy(true);
    try {
      await deleteVaultEntry(toDelete.id);
      setToDelete(null);
      void load();
    } catch {
      toast(t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  const from = (page - 1) * PAGE_SIZE + 1;
  const keyMissing = status !== null && !status.ready;

  const addBtn = canManage ? (
    <Btn tone="brand" onClick={() => setAdding(true)} disabled={keyMissing}>
      <Plus className="size-4" />
      {t("vault.add")}
    </Btn>
  ) : null;

  return (
    <PopHost>
      <PageHead title={t("vault.title")} sub={t("vault.intro")} actions={addBtn} />

      {keyMissing ? (
        <div className="crm-alert mb-4">
          <ShieldAlert className="size-4 shrink-0" />
          <span>{t(status?.reason === "invalid" ? "vault.keyInvalid" : "vault.keyMissing")}</span>
        </div>
      ) : null}

      <Toolbar>
        <SearchField value={qInput} onChange={setQInput} placeholder={t("vault.search")} />
        <MenuField
          value={kind}
          onChange={setKind}
          label={t("vault.kind")}
          options={[
            { value: "", label: t("common.all") },
            ...VAULT_KINDS.map((k) => ({ value: k, label: t(kindLabelKey(k)) })),
          ]}
        />
      </Toolbar>

      <div className="crm-card">
        {loading ? (
          <div className="flex justify-center py-16 text-muted-foreground">
            <Loader2 className="size-6 animate-spin" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={KeyRound}
            title={t("vault.empty")}
            body={t(keyMissing ? "vault.emptyNoKeyBody" : "vault.emptyBody")}
            action={addBtn}
          />
        ) : (
          <>
            <div className="crm-list-head" style={{ ...listCard(COLS), gridTemplateColumns: COLS, paddingTop: 22 }}>
              <span>{t("vault.col.entry")}</span>
              <span>{t("vault.client")}</span>
              <span>{t("vault.host")}</span>
              <span>{t("vault.username")}</span>
              <span>{t("vault.col.secret")}</span>
              <span>{t("vault.col.updated")}</span>
              <span />
            </div>

            <div className="crm-rows pt-0" style={listCard(COLS)}>
              {rows.map((entry) => (
                <VaultRow
                  key={entry.id}
                  entry={entry}
                  canManage={canManage}
                  onReveal={setRevealing}
                  onEdit={setEditing}
                  onDelete={setToDelete}
                />
              ))}
            </div>

            <div className="crm-tfoot">
              {t("vault.showing", { from, to: from + rows.length - 1, total })}
              <span className="crm-push flex gap-2.5">
                <Btn small disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  <ChevronLeft className="size-4" />
                  {t("common.prev")}
                </Btn>
                <Btn small disabled={page * PAGE_SIZE >= total} onClick={() => setPage((p) => p + 1)}>
                  {t("common.next")}
                  <ChevronRight className="size-4" />
                </Btn>
              </span>
            </div>
          </>
        )}
      </div>

      {adding || editing ? (
        <VaultModal
          entry={editing}
          onClose={() => {
            setAdding(false);
            setEditing(null);
          }}
          onSaved={() => void load()}
        />
      ) : null}

      {revealing ? <RevealDialog entry={revealing} onClose={() => setRevealing(null)} /> : null}

      {toDelete ? (
        <Modal
          title={t("vault.deleteTitle")}
          label={t("vault.deleteTitle")}
          onClose={() => setToDelete(null)}
          footer={
            <>
              <Btn onClick={() => setToDelete(null)} disabled={busy}>
                {t("common.cancel")}
              </Btn>
              <Btn tone="danger" onClick={() => void confirmDelete()} disabled={busy}>
                {busy ? <Loader2 className="size-4 animate-spin" /> : null}
                {t("common.delete")}
              </Btn>
            </>
          }
        >
          {/* Naming the row, because the confirm opens from a menu that has
              already closed over it - and because this is the one delete in the
              CRM whose contents cannot be reconstructed from anywhere else. */}
          <p className="crm-sub">
            {t("vault.deleteBody", { title: toDelete.title, client: toDelete.clientName ?? "-" })}
          </p>
        </Modal>
      ) : null}
    </PopHost>
  );
}
